import { QLookup, QModalHeader, QTitle, SearchResource } from '@qualio/ui-components';
import { globalSearchApi, qriApi } from '../../../../api';
import { QLookupStandardValueObject, StandardValueObject, SubComponentProps } from '../../EventFormField.types';
import * as DisplayStrings from '../../../../displayStrings';
import { ViewMap } from './DataView';
import { fieldTypeIsQObjectType, qObjectResourceMap } from '../../../../utils/eventFormUtils';
import { FieldTypeSchema } from '../../../../types/formFields/common';
import { RegistryItemStatus } from '../../../../types/registry';

const SEARCHABLE_STATUSES_CLAUSE = [{ terms: { status: [RegistryItemStatus.Effective] } }];

/**
 * The resource lookup modal is rendered as a sibling of EventFormField's <QFormControl>
 * (rather than as a child of it) on purpose. Chakra v2's `useRadio` inherits its `id` from
 * the nearest FormControlContext, and React context flows through portals. If this modal
 * rendered inside the form control, every QLookup radio would collapse onto the form
 * control's single id and become unselectable. Keeping it outside breaks that inheritance.
 */
const QObjectFieldModal = (props: SubComponentProps) => {
  const { fieldValues, inputType, onChange, fieldId, isEditModalOpen, setIsEditModalOpen, resourceSubType } = props;

  if (!fieldTypeIsQObjectType(inputType)) {
    return null;
  }

  if (!isEditModalOpen) {
    return null;
  }

  const valueObjects = fieldValues as StandardValueObject[];
  const parsedValueObjects = valueObjects.map((valueObject) => {
    return JSON.parse(valueObject.value) as QLookupStandardValueObject;
  });
  const qris = parsedValueObjects.map((value) => value.qri);

  const SelectComponent = props.multi ? QLookup.MultiSelect : QLookup.SingleSelect;

  return (
    <QLookup.DataProvider.QuickSearchV2
      resource={qObjectResourceMap[inputType] as SearchResource}
      resourceSubType={resourceSubType ?? undefined}
      must={inputType === FieldTypeSchema.enum.registry ? SEARCHABLE_STATUSES_CLAUSE : undefined}
      quickSearchClient={globalSearchApi}
      qriClient={qriApi}
      defaultDataQRIs={qris}
    >
      <SelectComponent
        data-cy="q-object-field-modal"
        accessors={{ id: 'id' }}
        defaultSortBy={{ id: 'name', desc: false }}
        isItemPreSelected={(item: QLookup.DataProvider.V2Result) => {
          return !!qris.find((qri) => qri === item.identifiers.qri);
        }}
        shouldPin={true}
        onSelect={async (selection) => {
          onChange(
            selection.map((selected) => {
              const existingValue = parsedValueObjects.find((fieldVal) => fieldVal.qri === selected.identifiers.qri);
              return {
                ...existingValue,
                value: JSON.stringify({
                  native_id: selected.id,
                  native_matrix_id: selected.identifiers.matrixId ?? undefined,
                  qri: selected.identifiers.qri,
                  domain: selected.domain,
                }),
                form_field_id: fieldId,
              };
            }),
          );
          setIsEditModalOpen(false);
        }}
        onCancel={() => {
          setIsEditModalOpen(false);
        }}
        action={DisplayStrings.ApplyChanges}
        view={ViewMap[inputType]}
        isOpen={isEditModalOpen}
      >
        <QModalHeader>
          <QTitle>{DisplayStrings.Add}</QTitle>
        </QModalHeader>
      </SelectComponent>
    </QLookup.DataProvider.QuickSearchV2>
  );
};

export default QObjectFieldModal;
